import { useAuth, useUser as useClerkUser } from "@clerk/clerk-react"
import { useDebouncedValue } from "@mantine/hooks"
import { useQuery } from "@tanstack/react-query"

import { AuthResponses } from "@costory/types/endpoints/auth"
import { User } from "@costory/types/user"

import { apiClientWithTimeout } from "@costory/front/lib/apiClient"

type AuthState =
  | {
      isLoading: true
      isSignedIn: undefined
      user: undefined
      isError: boolean
    }
  | {
      isLoading: false
      isSignedIn: false
      user: undefined
      isError: boolean
    }
  | {
      isLoading: false
      isSignedIn: true
      user: User
      isError: false
    }

export const useAuthState = (): AuthState => {
  const { isLoaded, isSignedIn, user: clerkUser } = useClerkUser()
  const { orgRole } = useAuth()

  const { data: user, isLoading: isMeLoading } = useQuery<
    User | undefined | null
  >({
    queryFn: async () => {
      if (!isLoaded || !isSignedIn) return undefined
      const response =
        await apiClientWithTimeout.get<AuthResponses.Me>("/auth/me")
      return response.data
    },
    queryKey: ["me", clerkUser?.id],
    enabled: isLoaded && isSignedIn,
    refetchOnWindowFocus(query) {
      return !query.state.data
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: true,
    refetchInterval: (_query) => {
      const shouldRefetch = isSignedIn && isLoaded
      return shouldRefetch ? 60000 : false
    },
  })
  const isLoading = !isLoaded || isMeLoading

  const result: AuthState = isLoading
    ? {
        isLoading: true,
        isSignedIn: undefined,
        user: undefined,
        isError: false,
      }
    : !isSignedIn || !user
      ? {
          isLoading: false,
          isSignedIn: false,
          user: undefined,
          isError: true,
        }
      : {
          isLoading,
          isSignedIn: true,
          user: {
            ...user!,
            isAdmin: orgRole?.includes("admin"),
          },
          isError: false,
        }

  // Always call useDebouncedValue to avoid hooks error
  const [debouncedResult] = useDebouncedValue(result, 5000)

  // Determine if we should debounce
  const shouldDebounce = !result.isSignedIn || !result.user

  // Return debounced result for non-error states, immediate result for error states
  return result.isError ? result : shouldDebounce ? debouncedResult : result
}
